import { Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TestEvents } from "../../../services/TestEvents";
import {
  CREATION_ACTIONS,
  CreationAction,
  IUserCreationPayload,
  IUserWithCustomPassword,
  ITestCreationRequest,
  ILocationLite,
} from "../../../interfaces/ITestEvent";
import { IUser, ICmUserInfo } from "../../../interfaces/IUser";
import { TestAuth } from "../../../services/TestAuth";
import { User } from "../../../services/User";

const SURVEY_ACTIONS: readonly CreationAction[] = ["signup_survey", "signup_nonmed_survey"];

@Component({
  selector: "test-events-creation",
  templateUrl: "./TestEventsCreationCmp.html",
  styleUrls: ["./TestEventsCreationCmp.css"],
})
export class TestEventsCreationCmp implements OnInit {
  entity: string = "Contact";
  action: CreationAction = "signup_reviewed";
  payload: IUserCreationPayload = {
    user: { id: 99999, __cm_users: [] } as IUserWithCustomPassword,
    location: {} as ILocationLite,
    source: "rust-crm",
  };
  password: string = "";
  tagsInput: string = "";

  selectedUser: IUser | null = null;
  resultMessage = "";
  resultType: "success" | "error" | "" = "";
  loadingUser = false;
  userIdError = "";
  readonly allActions = CREATION_ACTIONS;

  constructor(
    private route: ActivatedRoute,
    private testEvents: TestEvents,
    public testAuth: TestAuth,
    private userService: User
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      const a = params.get("action");
      if (a && (CREATION_ACTIONS as readonly string[]).includes(a)) {
        this.action = a as CreationAction;
      }
    });
  }

  isSurvey(): boolean {
    return (SURVEY_ACTIONS as readonly string[]).includes(this.action);
  }

  onUserIdBlur() {
    if (this.payload.user?.id && this.payload.user.id > 0) {
      this.loadUserById(this.payload.user.id);
    }
  }

  loadUserById(userId: number) {
    this.loadingUser = true;
    this.userIdError = "";
    this.userService.getFreshUserInfo(userId).subscribe({
      next: (user) => {
        this.loadingUser = false;
        this.selectedUser = user;
        this.populate(user);
      },
      error: () => {
        this.loadingUser = false;
        this.userIdError = "User not found";
      },
    });
  }

  onUserSelected(user: IUser) {
    if (!user) {
      this.selectedUser = null;
      return;
    }
    this.selectedUser = user;
    this.loadingUser = true;
    this.userService.getFreshUserInfo(user.id).subscribe({
      next: (fullUser) => {
        this.loadingUser = false;
        this.populate(fullUser);
      },
      error: () => {
        this.loadingUser = false;
        this.populate(user);
      },
    });
  }

  private populate(user: IUser) {
    const prevHash = this.payload.user?.password_hash;
    this.payload.user = {
      ...user,
      password_hash: prevHash,
      __cm_users: user.__cm_users ? [...user.__cm_users] : [],
    };
    if (user.cm_access_level > 0) this.entity = "ClientManager";
    else if (user.admin_access_level > 0) this.entity = "Admin";
    else if (user.distr_access_level > 0) this.entity = "Rep";
    else this.entity = "Contact";
  }

  addCmUser(user: IUser) {
    if (!user || !user.id) return;
    if (!this.payload.user.__cm_users) this.payload.user.__cm_users = [];
    if (this.payload.user.__cm_users.some((cm) => cm.id === user.id)) return;
    const cm: ICmUserInfo = {
      id: user.id,
      fname: user.fname,
      lname: user.lname,
      email: user.email,
    };
    this.payload.user.__cm_users = [...this.payload.user.__cm_users, cm];
  }

  removeCmUser(index: number) {
    if (!this.payload.user.__cm_users) return;
    this.payload.user.__cm_users = this.payload.user.__cm_users.filter((_, i) => i !== index);
  }

  private parseTags(): number[] | undefined {
    const raw = (this.tagsInput || "").trim();
    if (!raw) return undefined;
    const tags = raw
      .split(/[,\s]+/)
      .filter(Boolean)
      .map((t) => Number(t))
      .filter((n) => !isNaN(n));
    return tags.length ? tags : undefined;
  }

  private buildPayload(): IUserCreationPayload {
    if (this.isSurvey()) {
      return { user: this.payload.user };
    }
    const tags = this.parseTags();
    const location = this.hasLocationData() ? this.payload.location : undefined;
    return {
      user: this.payload.user,
      location,
      agreement_pdf_url: this.payload.agreement_pdf_url || undefined,
      review_session_id: this.payload.review_session_id || undefined,
      rep_email: this.payload.rep_email || undefined,
      comments: this.payload.comments || undefined,
      source: this.payload.source || undefined,
      tags,
    };
  }

  private hasLocationData(): boolean {
    const l = this.payload.location;
    if (!l) return false;
    return Object.values(l).some((v) => v !== undefined && v !== null && v !== "");
  }

  onSubmit() {
    this.resultMessage = "";
    this.resultType = "";
    const body: ITestCreationRequest = {
      entity: this.entity,
      action: this.action,
      password: this.password || undefined,
      payload: this.buildPayload(),
    };
    this.testEvents.sendCreation(body).subscribe({
      next: (r) => {
        this.resultType = "success";
        this.resultMessage = `Event sent. id=${r.event_id || "N/A"}`;
      },
      error: (err) => {
        this.resultType = "error";
        this.resultMessage = err.error?.error || "Failed to send event";
      },
    });
  }
}
